import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import { ExternalLink, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  defaultLandingContent,
  type LandingContent,
} from "@/data/landingContent";
import {
  useAdminLandingContent,
  useSaveLandingContent,
} from "@/hooks/useLandingContent";

export const Route = createFileRoute("/admin/content/")({
  component: AdminContentPage,
});

function AdminContentPage() {
  const { data, isLoading } = useAdminLandingContent();
  const save = useSaveLandingContent();
  const [draft, setDraft] = useState<LandingContent | null>(null);

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data, draft]);

  if (isLoading || !draft) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">Loading website content…</div>
    );
  }

  const patch = (updater: (d: LandingContent) => LandingContent) =>
    setDraft((d) => (d ? updater(d) : d));

  const onSave = () => {
    save.mutate(draft, {
      onSuccess: () => toast.success("Website content saved"),
      onError: () => toast.error("Failed to save website content"),
    });
  };

  const onReset = () => {
    if (window.confirm("Reset the editor to the built-in defaults? Nothing is saved until you click Save.")) {
      setDraft(defaultLandingContent);
    }
  };

  return (
    <div className="pb-24">
      <PageHeader
        title="Website Content"
        eyebrow="Support"
        subtitle="Edit the public landing page. Lists are edited as JSON; everything else is a plain field. Changes go live after saving."
        action={
          <>
            <Button variant="outline" asChild>
              <Link to="/" target="_blank">
                <ExternalLink />
                Preview
              </Link>
            </Button>
            <Button variant="outline" onClick={onReset}>
              <RotateCcw />
              Reset
            </Button>
            <Button onClick={onSave} disabled={save.isPending}>
              <Save />
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-5">
        {/* HERO */}
        <SectionCard title="Hero">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Social proof count"
              value={draft.hero.socialProofCount}
              onChange={(v) => patch((d) => ({ ...d, hero: { ...d.hero, socialProofCount: v } }))}
            />
            <TextField
              label="Social proof label"
              value={draft.hero.socialProofLabel}
              onChange={(v) => patch((d) => ({ ...d, hero: { ...d.hero, socialProofLabel: v } }))}
            />
          </div>
          <JsonField
            label="Hero slides"
            hint="Array of { eyebrow, title, emphasis, description, image, primaryLabel, primaryTo, secondaryLabel, secondaryAction, insightTitle, insightBody, log[] }"
            value={draft.hero.slides}
            onChange={(v) => patch((d) => ({ ...d, hero: { ...d.hero, slides: v } }))}
          />
          <JsonField
            label="Hero avatars"
            hint="Array of image URLs"
            value={draft.hero.avatars}
            onChange={(v) => patch((d) => ({ ...d, hero: { ...d.hero, avatars: v } }))}
          />
        </SectionCard>

        {/* PARTNER LOGOS */}
        <SectionCard title="Partner logos">
          <TextField
            label="Heading"
            value={draft.partnerHeading}
            onChange={(v) => patch((d) => ({ ...d, partnerHeading: v }))}
          />
          <JsonField
            label="Logos"
            hint="Array of names"
            value={draft.partnerLogos}
            onChange={(v) => patch((d) => ({ ...d, partnerLogos: v }))}
          />
        </SectionCard>

        {/* APPROACH / STATS */}
        <SectionCard title="Approach & stats">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Kicker"
              value={draft.approach.kicker}
              onChange={(v) => patch((d) => ({ ...d, approach: { ...d.approach, kicker: v } }))}
            />
            <TextField
              label="Button label"
              value={draft.approach.ctaLabel}
              onChange={(v) => patch((d) => ({ ...d, approach: { ...d.approach, ctaLabel: v } }))}
            />
            <TextField
              label="Title — first line"
              value={draft.approach.titleLine1}
              onChange={(v) => patch((d) => ({ ...d, approach: { ...d.approach, titleLine1: v } }))}
            />
            <TextField
              label="Title — emphasised line"
              value={draft.approach.titleEmphasis}
              onChange={(v) => patch((d) => ({ ...d, approach: { ...d.approach, titleEmphasis: v } }))}
            />
          </div>
          <TextAreaField
            label="Lead"
            value={draft.approach.lead}
            onChange={(v) => patch((d) => ({ ...d, approach: { ...d.approach, lead: v } }))}
          />
          <JsonField
            label="Stat cards"
            hint="Array of { label, value, delta, kind } where kind is chart-line | chart-bar-dark | chart-donut | status"
            value={draft.stats}
            onChange={(v) => patch((d) => ({ ...d, stats: v }))}
          />
        </SectionCard>

        {/* SERVICES */}
        <SectionCard title="Services">
          <HeadingFields
            kicker={draft.services.kicker}
            title={draft.services.title}
            lead={draft.services.lead}
            onKicker={(v) => patch((d) => ({ ...d, services: { ...d.services, kicker: v } }))}
            onTitle={(v) => patch((d) => ({ ...d, services: { ...d.services, title: v } }))}
            onLead={(v) => patch((d) => ({ ...d, services: { ...d.services, lead: v } }))}
          />
          <JsonField
            label="Service items"
            hint="Array of { title, text }. Icons are assigned by position."
            value={draft.services.items}
            onChange={(v) => patch((d) => ({ ...d, services: { ...d.services, items: v } }))}
          />
        </SectionCard>

        {/* HOW IT WORKS */}
        <SectionCard title="How it works">
          <HeadingFields
            kicker={draft.howItWorks.kicker}
            title={draft.howItWorks.title}
            lead={draft.howItWorks.lead}
            onKicker={(v) => patch((d) => ({ ...d, howItWorks: { ...d.howItWorks, kicker: v } }))}
            onTitle={(v) => patch((d) => ({ ...d, howItWorks: { ...d.howItWorks, title: v } }))}
            onLead={(v) => patch((d) => ({ ...d, howItWorks: { ...d.howItWorks, lead: v } }))}
          />
          <TextField
            label="Title — emphasised phrase"
            value={draft.howItWorks.titleEmphasis}
            onChange={(v) => patch((d) => ({ ...d, howItWorks: { ...d.howItWorks, titleEmphasis: v } }))}
          />
          <JsonField
            label="Steps"
            hint="Array of { number, title, text }"
            value={draft.howItWorks.steps}
            onChange={(v) => patch((d) => ({ ...d, howItWorks: { ...d.howItWorks, steps: v } }))}
          />
        </SectionCard>

        {/* AUDIENCES */}
        <SectionCard title="Audiences">
          <HeadingFields
            kicker={draft.audiences.kicker}
            title={draft.audiences.title}
            lead={draft.audiences.lead}
            onKicker={(v) => patch((d) => ({ ...d, audiences: { ...d.audiences, kicker: v } }))}
            onTitle={(v) => patch((d) => ({ ...d, audiences: { ...d.audiences, title: v } }))}
            onLead={(v) => patch((d) => ({ ...d, audiences: { ...d.audiences, lead: v } }))}
          />
          <JsonField
            label="Audience items"
            hint="Array of { title, text, points[], ctaLabel, ctaTo }"
            value={draft.audiences.items}
            onChange={(v) => patch((d) => ({ ...d, audiences: { ...d.audiences, items: v } }))}
          />
        </SectionCard>

        {/* SUCCESS STORIES */}
        <SectionCard title="Success stories">
          <HeadingFields
            kicker={draft.results.kicker}
            title={draft.results.title}
            lead={draft.results.lead}
            onKicker={(v) => patch((d) => ({ ...d, results: { ...d.results, kicker: v } }))}
            onTitle={(v) => patch((d) => ({ ...d, results: { ...d.results, title: v } }))}
            onLead={(v) => patch((d) => ({ ...d, results: { ...d.results, lead: v } }))}
          />
          <TextField
            label="Title — emphasised phrase"
            value={draft.results.titleEmphasis}
            onChange={(v) => patch((d) => ({ ...d, results: { ...d.results, titleEmphasis: v } }))}
          />
          <JsonField
            label="Stories"
            hint="Array of { tag, title, text, image }"
            value={draft.results.stories}
            onChange={(v) => patch((d) => ({ ...d, results: { ...d.results, stories: v } }))}
          />
        </SectionCard>

        {/* TESTIMONIALS */}
        <SectionCard title="Testimonials">
          <HeadingFields
            kicker={draft.testimonials.kicker}
            title={draft.testimonials.title}
            lead={draft.testimonials.lead}
            onKicker={(v) => patch((d) => ({ ...d, testimonials: { ...d.testimonials, kicker: v } }))}
            onTitle={(v) => patch((d) => ({ ...d, testimonials: { ...d.testimonials, title: v } }))}
            onLead={(v) => patch((d) => ({ ...d, testimonials: { ...d.testimonials, lead: v } }))}
          />
          <JsonField
            label="Featured testimonial"
            hint="Object { quote, name, role, image }"
            value={draft.testimonials.featured}
            onChange={(v) => patch((d) => ({ ...d, testimonials: { ...d.testimonials, featured: v } }))}
          />
          <JsonField
            label="Small testimonials"
            hint="Array of { quote, name, role, image }"
            value={draft.testimonials.items}
            onChange={(v) => patch((d) => ({ ...d, testimonials: { ...d.testimonials, items: v } }))}
          />
        </SectionCard>

        {/* FAQ */}
        <SectionCard title="FAQ">
          <HeadingFields
            kicker={draft.faq.kicker}
            title={draft.faq.title}
            lead={draft.faq.lead}
            onKicker={(v) => patch((d) => ({ ...d, faq: { ...d.faq, kicker: v } }))}
            onTitle={(v) => patch((d) => ({ ...d, faq: { ...d.faq, title: v } }))}
            onLead={(v) => patch((d) => ({ ...d, faq: { ...d.faq, lead: v } }))}
          />
          <JsonField
            label="Questions"
            hint="Array of { q, a }"
            value={draft.faq.items}
            onChange={(v) => patch((d) => ({ ...d, faq: { ...d.faq, items: v } }))}
          />
        </SectionCard>

        {/* CTA */}
        <SectionCard title="Call to action">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Title"
              value={draft.cta.title}
              onChange={(v) => patch((d) => ({ ...d, cta: { ...d.cta, title: v } }))}
            />
            <TextField
              label="Title — emphasised phrase"
              value={draft.cta.titleEmphasis}
              onChange={(v) => patch((d) => ({ ...d, cta: { ...d.cta, titleEmphasis: v } }))}
            />
            <TextField
              label="Primary button"
              value={draft.cta.primaryLabel}
              onChange={(v) => patch((d) => ({ ...d, cta: { ...d.cta, primaryLabel: v } }))}
            />
            <TextField
              label="Secondary button"
              value={draft.cta.secondaryLabel}
              onChange={(v) => patch((d) => ({ ...d, cta: { ...d.cta, secondaryLabel: v } }))}
            />
          </div>
          <TextAreaField
            label="Body"
            value={draft.cta.text}
            onChange={(v) => patch((d) => ({ ...d, cta: { ...d.cta, text: v } }))}
          />
        </SectionCard>

        {/* FOOTER */}
        <SectionCard title="Footer">
          <TextAreaField
            label="Tagline"
            value={draft.footer.tagline}
            onChange={(v) => patch((d) => ({ ...d, footer: { ...d.footer, tagline: v } }))}
          />
          <TextField
            label="Copyright"
            value={draft.footer.copyright}
            onChange={(v) => patch((d) => ({ ...d, footer: { ...d.footer, copyright: v } }))}
          />
          <JsonField
            label="Columns"
            hint="Array of { title, links: [{ label, href }] }. href starting with # scrolls; otherwise it is a normal link."
            value={draft.footer.columns}
            onChange={(v) => patch((d) => ({ ...d, footer: { ...d.footer, columns: v } }))}
          />
          <JsonField
            label="Socials"
            hint="Array of { platform, href } where platform is facebook | instagram | twitter | linkedin"
            value={draft.footer.socials}
            onChange={(v) => patch((d) => ({ ...d, footer: { ...d.footer, socials: v } }))}
          />
          <JsonField
            label="Legal links"
            hint="Array of labels"
            value={draft.footer.legal}
            onChange={(v) => patch((d) => ({ ...d, footer: { ...d.footer, legal: v } }))}
          />
        </SectionCard>
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-card p-5 md:p-6">
      <h2 className="mb-1 text-body-md font-medium text-foreground">{title}</h2>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function HeadingFields({
  kicker,
  title,
  lead,
  onKicker,
  onTitle,
  onLead,
}: {
  kicker: string;
  title: string;
  lead: string;
  onKicker: (v: string) => void;
  onTitle: (v: string) => void;
  onLead: (v: string) => void;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Kicker" value={kicker} onChange={onKicker} />
        <TextField label="Title" value={title} onChange={onTitle} />
      </div>
      <TextAreaField label="Lead" value={lead} onChange={onLead} />
    </>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-caption text-muted-foreground">{label}</span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-caption text-muted-foreground">{label}</span>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} />
    </label>
  );
}

// JsonField edits a nested list or object as pretty-printed JSON. It only
// commits valid JSON, so a half-typed value can never corrupt the draft.
function JsonField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (v: any) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value]);

  const commit = () => {
    try {
      const parsed = JSON.parse(text);
      setError(null);
      onChange(parsed);
    } catch {
      setError("Invalid JSON — fix it before the change is applied.");
    }
  };

  return (
    <div>
      <span className="mb-1.5 block text-caption text-muted-foreground">{label}</span>
      {hint && <p className="mb-2 text-caption text-muted-foreground/80">{hint}</p>}
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        rows={Math.min(18, Math.max(4, text.split("\n").length + 1))}
        spellCheck={false}
        className="font-mono text-[13px]"
      />
      {error && <p className="mt-1.5 text-caption text-destructive">{error}</p>}
    </div>
  );
}
